var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FilterType } from "../../model";
import { log } from "../../utils";
import { getAttachment, removeAttachment, setAttachment } from "../attachment";
import { filterAttributes, filterNode, filterText } from "../capture/filter";
import { Walker } from "../walker";
export class Observer {
    constructor(page) {
        this.page = page;
        this.isTracking = false;
        this.tracking = [];
        this.created = new Set();
        this.removed = [];
        this.attributes = new Set();
        this.texts = new Set();
        this.intersectionObserver = null;
        /** every now and then we compare current content with old one and send it if it is different */
        this.postProcessCanvas = new Map();
        this.postProcessFrames = [];
        this.postProcessInputs = [];
        this.postProcessScroll = [];
        this.postProcessStyles = [];
        this.postProcessStylePointers = [];
        /** some styles are applied with delay, check N times max if any are added to shadow roots with none */
        this.postLookupStyles = new Map();
        this.sheetCounter = 1;
        this.sheetIds = new WeakMap();
    }
    /**
     * Registers observer on element and process each record list
     */
    track(target) {
        var _a, _b;
        (_a = this.observer) !== null && _a !== void 0 ? _a : (this.observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                switch (mutation.type) {
                    case "attributes":
                        this.processAttributes(mutation);
                        break;
                    case "characterData":
                        this.processCharacterData(mutation);
                        break;
                    case "childList":
                        this.processChildList(mutation);
                        break;
                }
            }
        }));
        (_b = this.intersectionObserver) !== null && _b !== void 0 ? _b : (this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                var _a;
                const cnvInfo = this.postProcessCanvas.get(entry.target);
                if (!cnvInfo) {
                    (_a = this.intersectionObserver) === null || _a === void 0 ? void 0 : _a.unobserve(entry.target);
                }
                else {
                    cnvInfo.isVisible = entry.isIntersecting;
                }
            });
        }));
        if (!this.tracking.includes(target)) {
            this.tracking.push(target);
            this.observe(target);
            this.isTracking = true;
        }
    }
    observe(target) {
        this.page.animate.add(target);
        this.created.add(target);
        this.observer.observe(target, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
            attributeOldValue: false,
            characterDataOldValue: false,
        });
    }
    untrack() {
        var _a, _b;
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
        (_b = this.intersectionObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
        this.isTracking = false;
        this.tracking = [];
        this.postProcessCanvas.clear();
        this.postLookupStyles.clear();
    }
    processAttributes(mutation) {
        const props = getAttachment(mutation.target);
        if (props && props.filter !== FilterType.NoDisplay) {
            props.attributes[mutation.attributeName] = this.page.serializer.attribute(mutation.target, mutation.attributeName);
            if (props.id) {
                this.attributes.add(mutation.target);
            }
        }
    }
    processCharacterData(mutation) {
        const props = getAttachment(mutation.target);
        if (props) {
            props.text = this.page.serializer.text(mutation.target);
            if (props.id) {
                this.texts.add(mutation.target);
            }
        }
    }
    processChildList(mutation) {
        const props = getAttachment(mutation.target);
        // non-attached nodes will either be scanned by a parent or ignored by filter
        // also skip filtered direct parent
        if (!props ||
            props.filter === FilterType.Display ||
            props.filter === FilterType.NoDisplay) {
            return;
        }
        mutation.removedNodes.forEach((node) => {
            const props = getAttachment(node);
            // non-attached nodes have been inserted and removed in same cycle
            if (props) {
                new Walker(node).sprint((child) => {
                    var _a;
                    this.page.idp.removeId((_a = getAttachment(child)) === null || _a === void 0 ? void 0 : _a.id);
                    removeAttachment(child);
                });
                if (props.id) {
                    this.removed.push(props.id);
                }
            }
        });
        mutation.addedNodes.forEach((node) => this.created.add(node));
    }
    /** is element visible in viewport */
    inViewport(elm) {
        return this.postProcessCanvas.get(elm).isVisible;
    }
    postProcess() {
        var _a, _b;
        const updates = [];
        for (const [cnv, info] of this.postProcessCanvas) {
            if (!cnv.isConnected) {
                this.postProcessCanvas.delete(cnv);
            }
            else if (Date.now() - info.ts > 1200 && this.inViewport(cnv)) {
                info.ts = Date.now();
                (_b = (_a = this.page
                    .getWindow()).createImageBitmap) === null || _b === void 0 ? void 0 : _b.call(_a, cnv).then((bitmap) => {
                    this.page.messenger.sendPageUpdate({
                        type: "CanvasImageBitmap",
                        bitmap,
                        height: cnv.height,
                        width: cnv.width,
                        id: this.page.idp.getId(cnv),
                        document: this.page.documentId,
                    }, [bitmap]);
                }).catch((ex) => log(ex));
            }
        }
        for (const frame of this.postProcessFrames) {
            this.page.trackFrame(frame);
        }
        this.postProcessFrames = [];
        for (let [shadowRoot, tries] of this.postLookupStyles) {
            if (tries--) {
                this.trackAdoptedSheets(shadowRoot);
                this.postLookupStyles.set(shadowRoot, tries);
            }
            else {
                this.postLookupStyles.delete(shadowRoot);
            }
        }
        for (const style of this.postProcessStyles) {
            if (style.sheet.rules)
                updates.push(this.page.event("StyleSheet", {
                    id: style.id,
                    target: style.target,
                    format: "text",
                    href: style.sheet.href,
                    rules: style.sheet.rules,
                }));
        }
        this.postProcessStyles = [];
        for (const style of this.postProcessStylePointers) {
            updates.push(this.page.event("StyleSheet", style));
        }
        this.postProcessStylePointers = [];
        for (const input of this.postProcessInputs) {
            this.page.input.capture({ target: input });
            switch (input.tagName) {
                case "INPUT":
                    this.page.input.hookInstance(input, "checked");
                // fallthrough
                case "TEXTAREA":
                case "SELECT":
                    this.page.input.hookInstance(input, "value");
                    break;
                case "OPTION":
                    this.page.input.hookInstance(input, "selected");
                    break;
            }
        }
        this.postProcessInputs = [];
        for (const scroll of this.postProcessScroll) {
            this.page.scroll.capture({ target: scroll });
        }
        this.postProcessScroll = [];
        return { events: updates };
    }
    trackAdoptedSheets(node, target = this.page.idp.getId(node)) {
        for (const sheet of node.adoptedStyleSheets) {
            if (this.sheetIds.has(sheet)) {
                this.postProcessStylePointers.push({
                    id: this.sheetIds.get(sheet),
                    target,
                    format: "pointer",
                    rules: "",
                });
            }
            else {
                this.sheetIds.set(sheet, this.sheetCounter);
                this.postProcessStyles.push({
                    id: this.sheetCounter++,
                    target,
                    sheet: { rules: this.page.serializer.stylesheet(sheet) },
                });
            }
        }
    }
    visit(node, filter) {
        var _a, _b, _c, _d, _e;
        const props = setAttachment(node, node.nodeType === node.ELEMENT_NODE
            ? this.page.serializer.attributes(node)
            : undefined, node.nodeType === node.TEXT_NODE
            ? this.page.serializer.text(node)
            : undefined, filter);
        const info = this.page.serializer.node(node, props);
        // deal with shadow roots which have their own separate dom tree
        if (((_a = node.shadowRoot) === null || _a === void 0 ? void 0 : _a.nodeType) ===
            node.DOCUMENT_FRAGMENT_NODE &&
            (filter === FilterType.None || filter === FilterType.DisplayRandomized)) {
            this.observe(node.shadowRoot);
        }
        else if (node.nodeType === node.DOCUMENT_FRAGMENT_NODE) {
            this.page.cursor.track(node);
            if (filter === FilterType.None)
                this.page.input.track(node);
            if ((_b = node.adoptedStyleSheets) === null || _b === void 0 ? void 0 : _b.length) {
                this.trackAdoptedSheets(node, info.id);
            }
            else {
                this.postLookupStyles.set(node, 3);
            }
        }
        // filter and postprocess
        switch (props.filter) {
            case FilterType.None:
                if (node.nodeType === node.ELEMENT_NODE) {
                    switch (info.name) {
                        case "CANVAS":
                            this.postProcessCanvas.set(node, {
                                ts: 0,
                                data: null,
                                pending: false,
                                isVisible: false,
                                format: "image/webp",
                            });
                            this.intersectionObserver.observe(node);
                            break;
                        case "IFRAME":
                            if (((_c = node.contentDocument) === null || _c === void 0 ? void 0 : _c.contentType) ===
                                "application/pdf") {
                                break;
                            }
                            else if ((_e = (_d = info.attrs) === null || _d === void 0 ? void 0 : _d.src) === null || _e === void 0 ? void 0 : _e.includes(".pdf")) {
                                const url = new URL(info.attrs.src, location.href);
                                if (url.pathname.toLowerCase().endsWith(".pdf")) {
                                    break;
                                }
                            }
                            this.postProcessFrames.push(node);
                            break;
                        case "INPUT":
                        case "OPTION":
                        case "TEXTAREA":
                        case "SELECT":
                            this.postProcessInputs.push(node);
                            break;
                        case "STYLE":
                            if (info.sheet) {
                                this.postProcessStyles.push({
                                    id: this.sheetCounter++,
                                    target: info.id,
                                    sheet: info.sheet,
                                });
                                delete info.sheet;
                            }
                            break;
                        case "NOSCRIPT":
                            this.page.filter.filterNode(node, (props.filter = FilterType.NoDisplay));
                            break;
                        default:
                            if (this.page.scroll.isScrollable(node)) {
                                this.postProcessScroll.push(node);
                            }
                    }
                }
                break;
            case FilterType.Display: {
                info.filter = props.filter;
                // Get computed styles
                const computedStyle = this.page
                    .getWindow()
                    .getComputedStyle(node);
                // Width and height including padding and border, but excluding margin
                const width = node.offsetWidth;
                const height = node.offsetHeight;
                // Get margins
                const marginTop = parseFloat(computedStyle.marginTop);
                const marginBottom = parseFloat(computedStyle.marginBottom);
                const marginLeft = parseFloat(computedStyle.marginLeft);
                const marginRight = parseFloat(computedStyle.marginRight);
                // make remote of similar initial size, later changes are not synced
                info.attrs.style = `\
border:none;\
width:${width}px;\
height:${height}px;\
display:${info.name === "IFRAME" ? "inline-block" : computedStyle.display};\
margin:${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px;\
${info.attrs.style || ""}\
`;
                if (info.attrs)
                    info.attrs = filterAttributes(info.attrs, node);
                break;
            }
            case FilterType.DisplayRandomized:
                info.filter = props.filter;
                filterNode(info, node);
                if (node.nodeType === node.ELEMENT_NODE) {
                    switch (info.name) {
                        case "STYLE":
                            this.postProcessStyles.push({
                                id: this.sheetCounter++,
                                target: info.id,
                                sheet: info.sheet,
                            });
                            break;
                        case "LINK":
                            info.attrs.href = props.attributes.href;
                            break;
                        case "SVG":
                            info.filter = FilterType.None;
                            break;
                        case "NOSCRIPT":
                            this.page.filter.filterNode(node, (props.filter = FilterType.NoDisplay));
                            break;
                        default:
                            if (this.page.scroll.isScrollable(node)) {
                                this.postProcessScroll.push(node);
                            }
                    }
                }
                break;
            case FilterType.NoDisplay:
                info.filter = props.filter;
                info.name = "#comment";
                break;
        }
        // reset props for next cycle
        props.attributes = props.attributes ? {} : undefined;
        props.text = undefined;
        return info;
    }
    deepScan(walker, nodes, options = { filter: FilterType.None }) {
        const current = this.visit(walker.currentNode, options.filter);
        nodes[current.id] = current;
        if ((options.filter === FilterType.None ||
            options.filter === FilterType.DisplayRandomized) &&
            walker.firstChild()) {
            const tree = [
                current.id,
                this.deepScan(walker, nodes, {
                    filter: options.filter || this.page.filter.getFilter(walker.currentNode),
                }),
            ];
            while (walker.nextSibling())
                tree.push(this.deepScan(walker, nodes, {
                    filter: options.filter || this.page.filter.getFilter(walker.currentNode),
                }));
            walker.parentNode();
            return tree;
        }
        else {
            return current.id;
        }
    }
    /** id and calculate next fragment in target's children */
    identify(walker, parent) {
        let next;
        const tree = [];
        const nodes = {};
        const previous = walker.currentNode.previousSibling;
        // scan new fragment of siblings
        do {
            next = walker.currentNode.nextSibling;
            tree.push(this.deepScan(walker, nodes, {
                filter: this.page.filter.getFilter(parent) ||
                    this.page.filter.getFilter(walker.currentNode),
            }));
        } while (walker.nextSibling() && !this.page.idp.getId(walker.currentNode));
        return {
            parent: this.page.idp.getId(parent),
            next: this.page.idp.getId(next),
            previous: this.page.idp.getId(previous),
            tree,
            nodes,
        };
    }
    flush() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const insert = [];
            const remove = this.removed;
            // assign ids to nodes and collect their info
            for (const node of this.created) {
                let parent = node.parentNode;
                let walker;
                if (
                // removed already
                !node.isConnected ||
                    (
                    // scanned already
                    (_a = getAttachment(node)) === null || _a === void 0 ? void 0 : _a.id) ||
                    // parent has not been scanned, this will be part of another fragment
                    (parent !== this.page.getDocument() &&
                        ((parent && !((_b = getAttachment(parent)) === null || _b === void 0 ? void 0 : _b.id)) ||
                            // previous has not been scanned, this will be part of another fragment
                            (node.previousSibling && !((_c = getAttachment(node.previousSibling)) === null || _c === void 0 ? void 0 : _c.id))))) {
                    continue;
                }
                this.page.filter.scan(node);
                if (parent === this.page.getDocument()) {
                    walker = document.createTreeWalker(parent.documentElement);
                }
                else if (node.nodeType === document.DOCUMENT_FRAGMENT_NODE) {
                    parent = node.host;
                    walker = document.createTreeWalker(node);
                }
                else {
                    walker = document.createTreeWalker(parent);
                    walker.firstChild();
                    while (walker.currentNode !== node && walker.nextSibling())
                        ;
                }
                insert.push(this.identify(walker, parent));
            }
            // collect attributes changes of old nodes
            const attributes = this.attributes.size
                ? {}
                : undefined;
            for (const node of this.attributes) {
                const att = getAttachment(node);
                if (att === null || att === void 0 ? void 0 : att.id) {
                    attributes[att.id] =
                        att.filter === FilterType.DisplayRandomized ||
                            att.filter === FilterType.Display
                            ? filterAttributes(att.attributes, node)
                            : att.attributes;
                }
            }
            // collect text changes of old nodes
            const values = this.texts.size ? {} : undefined;
            for (const node of this.texts) {
                const att = getAttachment(node);
                if (att === null || att === void 0 ? void 0 : att.id) {
                    values[att.id] =
                        att.filter === FilterType.DisplayRandomized
                            ? filterText(att.text)
                            : att.text;
                }
            }
            const nodes = insert.length || remove.length ? { insert, remove } : undefined;
            this.created = new Set();
            this.removed = [];
            this.texts = new Set();
            this.attributes = new Set();
            return nodes || attributes || values
                ? {
                    nodes,
                    attributes,
                    values,
                }
                : null;
        });
    }
}
//# sourceMappingURL=observer.js.map